import { AdminAuditLog } from '../models/admin-audit-log.model';
import { AdminCoupon, AdminPromotion } from '../models/admin-marketing.model';
import { AdminRoom, AdminRoomAuditEntry } from '../models/admin-room.model';
import { AdminSetting } from '../models/admin-setting.model';
import { AdminUser } from '../models/admin-user.model';
import { extractApiList } from './api-list.util';

function readString(value: unknown): string {
  return value == null ? '' : String(value);
}

function readNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function readBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off', ''].includes(normalized)) return false;
  }
  return fallback;
}

function readNested(record: unknown): Record<string, unknown> | null {
  return record && typeof record === 'object' ? (record as Record<string, unknown>) : null;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => readString(item)).filter(Boolean);
}

function pick(raw: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    if (raw[key] != null) return raw[key];
  }
  return undefined;
}

export function normalizeAdminAuditLog(raw: Record<string, unknown>): AdminAuditLog {
  const actor = readNested(pick(raw, 'actor', 'admin', 'user', 'performed_by'));
  const metadata = readNested(raw['metadata']);

  return {
    id: readString(pick(raw, 'id')),
    action: readString(pick(raw, 'action', 'event', 'type') || 'UNKNOWN'),
    entity_type: readString(pick(raw, 'entity_type', 'auditable_type', 'model_type', 'subject_type')),
    entity_id: readString(pick(raw, 'entity_id', 'auditable_id', 'model_id', 'subject_id')),
    reason: pick(raw, 'reason', 'notes') != null ? readString(pick(raw, 'reason', 'notes')) : null,
    metadata,
    actor: actor
      ? {
          id: readString(actor['id']),
          name: readString(actor['name'] || actor['full_name']),
          email: actor['email'] != null ? readString(actor['email']) : undefined,
        }
      : null,
    created_at: readString(pick(raw, 'created_at', 'timestamp', 'occurred_at')),
  };
}

export function extractAdminAuditLogs(response: { data?: unknown } | null | undefined): AdminAuditLog[] {
  return extractApiList<Record<string, unknown>>(response?.data).map((item) => normalizeAdminAuditLog(item));
}

export function normalizeAdminSetting(raw: Record<string, unknown>): AdminSetting {
  const updatedBy = readNested(raw['updated_by']);
  const type = readString(raw['type'] || 'STRING').toUpperCase();
  // BOOLEAN defaults to false when missing/ambiguous — maintenance must never default ON.
  const value =
    type === 'BOOLEAN'
      ? (readBoolean(raw['value'], false) ? 'true' : 'false')
      : raw['value'] == null
        ? ''
        : typeof raw['value'] === 'string'
          ? raw['value']
          : JSON.stringify(raw['value']);

  return {
    key: readString(raw['key']),
    value,
    type,
    description: raw['description'] != null ? readString(raw['description']) : null,
    updated_at: raw['updated_at'] != null ? readString(raw['updated_at']) : null,
    updated_by: updatedBy
      ? { id: readString(updatedBy['id']), name: readString(updatedBy['name']) }
      : null,
  };
}

export function extractAdminSettings(response: { data?: unknown } | null | undefined): AdminSetting[] {
  const data = response?.data;
  if (Array.isArray(data)) {
    return data
      .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
      .map((item) => normalizeAdminSetting(item));
  }
  return extractApiList<Record<string, unknown>>(data).map((item) => normalizeAdminSetting(item));
}

export function normalizeAdminRoom(raw: Record<string, unknown>): AdminRoom {
  const establishment = readNested(pick(raw, 'establishment', 'hotel'));
  const priceRaw = pick(raw, 'price', 'price_per_night', 'base_price');

  const estName =
    (establishment && readString(pick(establishment, 'name', 'title'))) ||
    readString(pick(raw, 'establishment_name', 'establishmentName', 'hotel_name', 'hotelName', 'establishment_title')) ||
    '';

  const estId = readString(pick(raw, 'establishment_id', 'establishmentId') || establishment?.['id']);
  const acceptedPaymentMethods = readStringArray(pick(raw, 'accepted_payment_methods', 'acceptedPaymentMethods'));
  const acceptedCurrencies = readStringArray(pick(raw, 'accepted_currencies', 'acceptedCurrencies'));

  return {
    id: readString(raw['id']),
    establishment_id: estId,
    name: readString(pick(raw, 'name', 'title') || 'Chambre'),
    price: priceRaw == null || priceRaw === '' ? null : readNumber(priceRaw),
    admin_status: readString(pick(raw, 'admin_status', 'adminStatus') || 'ACTIVE').toUpperCase(),
    status: readString(pick(raw, 'status') || 'AVAILABLE').toUpperCase(),
    created_at: raw['created_at'] != null ? readString(raw['created_at']) : null,
    establishment: estName || estId ? { id: estId, name: estName } : null,
    accepted_payment_methods: acceptedPaymentMethods.length > 0 ? acceptedPaymentMethods : ['onsite', 'online'],
    accepted_currencies: acceptedCurrencies.length > 0 ? acceptedCurrencies : ['USD', 'CDF'],
  };
}

export function extractAdminRooms(response: { data?: unknown } | null | undefined): AdminRoom[] {
  return extractApiList<Record<string, unknown>>(response?.data).map((item) => normalizeAdminRoom(item));
}

export function extractAdminRoomAuditEntries(data: unknown): AdminRoomAuditEntry[] {
  return extractApiList<Record<string, unknown>>(data).map((raw) => {
    const admin = readNested(pick(raw, 'admin', 'actor', 'user'));
    return {
      id: raw['id'] != null ? readString(raw['id']) : undefined,
      action: raw['action'] != null ? readString(raw['action']) : undefined,
      reason: raw['reason'] != null ? readString(raw['reason']) : null,
      admin: admin
        ? {
            id: admin['id'] != null ? readString(admin['id']) : undefined,
            name: admin['name'] != null ? readString(admin['name']) : undefined,
            email: admin['email'] != null ? readString(admin['email']) : undefined,
          }
        : null,
      created_at: raw['created_at'] != null ? readString(raw['created_at']) : undefined,
      metadata: readNested(raw['metadata']) ?? undefined,
    };
  });
}

export function canAdminDisableRoom(room: AdminRoom): boolean {
  return !['DISABLED', 'INACTIVE', 'SUSPENDED'].includes(room.admin_status.toUpperCase());
}

export function canAdminEnableRoom(room: AdminRoom): boolean {
  return ['DISABLED', 'INACTIVE', 'SUSPENDED'].includes(room.admin_status.toUpperCase());
}

function normalizeEstablishmentRef(raw: Record<string, unknown> | null) {
  if (!raw) return null;
  return {
    id: readString(raw['id']),
    name: readString(raw['name']),
    partner: raw['partner'] != null ? readString(raw['partner']) : null,
  };
}

export function normalizeAdminCoupon(raw: Record<string, unknown>): AdminCoupon {
  const establishment = readNested(pick(raw, 'establishment'));
  return {
    id: readString(raw['id']),
    establishment_id: readString(
      pick(raw, 'establishment_id', 'establishmentId') || establishment?.['id'],
    ),
    code: readString(pick(raw, 'code')),
    name: readString(pick(raw, 'name')),
    description: pick(raw, 'description') != null ? readString(pick(raw, 'description')) : null,
    discount_percentage: readNumber(pick(raw, 'discount_percentage', 'discountPercentage')),
    applies_to: readString(pick(raw, 'applies_to', 'appliesTo') || 'ALL'),
    room_type: pick(raw, 'room_type', 'roomType') != null ? readString(pick(raw, 'room_type', 'roomType')) : null,
    room_ids: readStringArray(pick(raw, 'room_ids', 'roomIds')),
    max_uses: pick(raw, 'max_uses', 'maxUses') != null ? readNumber(pick(raw, 'max_uses', 'maxUses')) : null,
    used_count: readNumber(pick(raw, 'used_count', 'usedCount')),
    expires_at: pick(raw, 'expires_at', 'expiresAt') != null ? readString(pick(raw, 'expires_at', 'expiresAt')) : null,
    is_active: readBoolean(pick(raw, 'is_active', 'isActive'), true),
    is_valid: readBoolean(pick(raw, 'is_valid', 'isValid'), true),
    establishment: normalizeEstablishmentRef(establishment),
    created_at: pick(raw, 'created_at', 'createdAt') != null ? readString(pick(raw, 'created_at', 'createdAt')) : null,
    updated_at: pick(raw, 'updated_at', 'updatedAt') != null ? readString(pick(raw, 'updated_at', 'updatedAt')) : null,
  };
}

export function extractAdminCoupons(response: { data?: unknown } | null | undefined): AdminCoupon[] {
  return extractApiList<Record<string, unknown>>(response?.data).map((item) => normalizeAdminCoupon(item));
}

export function normalizeAdminPromotion(raw: Record<string, unknown>): AdminPromotion {
  const establishment = readNested(pick(raw, 'establishment'));
  return {
    id: readString(raw['id']),
    establishment_id: readString(
      pick(raw, 'establishment_id', 'establishmentId') || establishment?.['id'],
    ),
    name: readString(pick(raw, 'name')),
    description: pick(raw, 'description') != null ? readString(pick(raw, 'description')) : null,
    discount_percentage: readNumber(pick(raw, 'discount_percentage', 'discountPercentage')),
    applies_to: readString(pick(raw, 'applies_to', 'appliesTo') || 'ALL'),
    room_type: pick(raw, 'room_type', 'roomType') != null ? readString(pick(raw, 'room_type', 'roomType')) : null,
    room_ids: readStringArray(pick(raw, 'room_ids', 'roomIds')),
    start_date: pick(raw, 'start_date', 'startDate') != null ? readString(pick(raw, 'start_date', 'startDate')) : null,
    end_date: pick(raw, 'end_date', 'endDate') != null ? readString(pick(raw, 'end_date', 'endDate')) : null,
    is_active: readBoolean(pick(raw, 'is_active', 'isActive'), true),
    is_currently_active: readBoolean(pick(raw, 'is_currently_active', 'isCurrentlyActive'), false),
    establishment: normalizeEstablishmentRef(establishment),
    created_at: pick(raw, 'created_at', 'createdAt') != null ? readString(pick(raw, 'created_at', 'createdAt')) : null,
    updated_at: pick(raw, 'updated_at', 'updatedAt') != null ? readString(pick(raw, 'updated_at', 'updatedAt')) : null,
  };
}

export function extractAdminPromotions(response: { data?: unknown } | null | undefined): AdminPromotion[] {
  return extractApiList<Record<string, unknown>>(response?.data).map((item) => normalizeAdminPromotion(item));
}

export function trimToNull(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed ? trimmed : null;
}

function readStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => readString(item).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        return readStringList(JSON.parse(trimmed));
      } catch {
        return [trimmed];
      }
    }
    return trimmed.split(/[,|]/).map((item) => item.trim()).filter(Boolean);
  }
  return [];
}

export function normalizeAdminDetail(raw: unknown): AdminUser {
  const data = readNested(raw) ?? {};
  const roles = readStringList(pick(data, 'roles', 'role'));
  const permissions = readStringList(pick(data, 'permissions', 'permission'));

  return {
    id: readString(pick(data, 'id')),
    name: readString(pick(data, 'name', 'full_name', 'fullName')),
    email: readString(pick(data, 'email')),
    status: readString(pick(data, 'status') || 'ACTIVE'),
    partner_status: readString(pick(data, 'partner_status', 'partnerStatus') || 'NONE'),
    roles,
    account_type: pick(data, 'account_type', 'accountType') != null
      ? readString(pick(data, 'account_type', 'accountType'))
      : undefined,
    is_email_verified: readBoolean(
      pick(data, 'is_email_verified', 'isEmailVerified'),
      pick(data, 'email_verified_at', 'emailVerifiedAt') != null,
    ),
    is_active: readBoolean(pick(data, 'is_active', 'isActive'), true),
    created_at: readString(pick(data, 'created_at', 'createdAt')),
    updated_at: readString(pick(data, 'updated_at', 'updatedAt')),
    avatar_url: pick(data, 'avatar_url', 'avatarUrl') != null
      ? readString(pick(data, 'avatar_url', 'avatarUrl'))
      : null,
    permissions,
    has_2fa_enabled: readBoolean(
      pick(data, 'has_2fa_enabled', 'has2faEnabled', 'is_two_factor_enabled', 'two_factor_enabled', 'twoFactorEnabled'),
      false,
    ),
    phone: pick(data, 'phone', 'phone_number', 'phoneNumber') != null
      ? readString(pick(data, 'phone', 'phone_number', 'phoneNumber'))
      : null,
    last_login_at: pick(data, 'last_login_at', 'lastLoginAt') != null
      ? readString(pick(data, 'last_login_at', 'lastLoginAt'))
      : null,
    banned_at: pick(data, 'banned_at', 'bannedAt') != null
      ? readString(pick(data, 'banned_at', 'bannedAt'))
      : null,
    email_verified_at: pick(data, 'email_verified_at', 'emailVerifiedAt') != null
      ? readString(pick(data, 'email_verified_at', 'emailVerifiedAt'))
      : null,
  };
}
